import { useQuery } from "@tanstack/react-query";
import { Trophy } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getNationRivalry } from "@/lib/social.functions";
import { AnimatedNumber } from "./PulseDeltaBadge";
import { shouldUseFoundationFallback, MOCK_NATION_RIVALRY_ROWS } from "@/lib/demo/executiveDemoMock";

const FLAG: Record<string, string> = {
  ARG: "🇦🇷", BRA: "🇧🇷", MEX: "🇲🇽", USA: "🇺🇸", CAN: "🇨🇦",
  ESP: "🇪🇸", FRA: "🇫🇷", GER: "🇩🇪", ITA: "🇮🇹", POR: "🇵🇹",
  ENG: "🏴󠁧󠁢󠁥󠁮󠁧󠁿", URU: "🇺🇾", COL: "🇨🇴", CHI: "🇨🇱", PER: "🇵🇪",
  ECU: "🇪🇨", JPN: "🇯🇵", KOR: "🇰🇷", AUS: "🇦🇺", MAR: "🇲🇦",
  NED: "🇳🇱", BEL: "🇧🇪", CRO: "🇭🇷", SUI: "🇨🇭", SEN: "🇸🇳",
};

/**
 * Top-5 nations ranked by accumulated hype (active fans tiebreak).
 * Data: `nation_rivalry_stats` via getNationRivalry server fn.
 */
export function NationLeaderboard() {
  const q = useQuery({
    queryKey: ["worldPulse", "nations"],
    queryFn: () => getNationRivalry(),
    refetchInterval: 12_000,
  });

  const liveRows = (q.data?.rows ?? []).slice(0, 5);
  const rows = liveRows.length === 0 && shouldUseFoundationFallback()
    ? MOCK_NATION_RIVALRY_ROWS.slice(0, 5)
    : liveRows;
  const max = rows[0]?.hype_total ?? 0;

  // Fallback ranking (Pre-Mundial): real registered nations alphabetical.
  const fallback = useQuery({
    queryKey: ["worldPulse", "nationsFallback"],
    queryFn: async () => {
      const { data } = await supabase
        .from("worldcup_teams")
        .select("code, name")
        .order("name", { ascending: true })
        .limit(5);
      return data ?? [];
    },
    enabled: rows.length === 0,
  });
  const isEmpty = rows.length === 0;
  const fbRows = fallback.data ?? [];

  return (
    <section
      aria-label="Ranking de Naciones"
      className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[oklch(0.14_0.05_30)] to-[oklch(0.10_0.03_280)] p-4 shadow-[0_20px_60px_-20px_rgba(0,0,0,.6)]"
    >
      <header className="mb-3 flex items-center gap-2">
        <Trophy className="h-4 w-4 text-amber-400" />
        <h2 className="text-xs font-black uppercase tracking-[0.18em] text-white/90">Ranking de Naciones</h2>
      </header>

      {isEmpty ? (
        <ol className="space-y-2">
          {(fbRows.length > 0 ? fbRows : Array.from({ length: 5 }).map((_, i) => ({ code: `N${i+1}`, name: "Nación registrada" }))).map((t, i) => {
            const code = (t as { code?: string }).code ?? "??";
            const name = (t as { name?: string }).name ?? code;
            const rank = i + 1;
            return (
              <li key={code} className="group relative overflow-hidden rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
                <div className="flex items-center gap-3">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-white/15 to-white/5 text-[11px] font-black text-white/80">
                    #{rank}
                  </span>
                  <span className="text-xl leading-none">{FLAG[code] ?? "🏳️"}</span>
                  <span className="truncate text-sm font-bold text-white">{name}</span>
                  <span className="ml-auto rounded-full border border-amber-400/40 bg-amber-500/10 px-2 py-0.5 text-[9px] font-black uppercase tracking-wider text-amber-300">
                    Lista para batalla
                  </span>
                </div>
              </li>
            );
          })}
          <li className="pt-1 text-center text-[10px] uppercase tracking-wider text-white/40">
            Ranking inicial · se activa con el primer partido
          </li>
        </ol>
      ) : (
        <ol className="space-y-2">
          {rows.map((r, i) => {
            const hype = r.hype_total ?? 0;
            const code = r.nation_code ?? "??";
            const pct = max > 0 ? Math.max(6, Math.round((hype / max) * 100)) : 6;
            const rank = i + 1;
            const rankTone =
              rank === 1 ? "from-amber-300 to-yellow-500 text-black"
              : rank === 2 ? "from-slate-200 to-slate-400 text-black"
              : rank === 3 ? "from-orange-400 to-amber-600 text-black"
              : "from-white/15 to-white/5 text-white/80";
            return (
              <li key={code} className="group relative overflow-hidden rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
                <div
                  className="pointer-events-none absolute inset-y-0 left-0 -z-10 bg-gradient-to-r from-primary/30 to-fuchsia-500/20 transition-all duration-700"
                  style={{ width: `${pct}%` }}
                />
                <div className="flex items-center gap-3">
                  <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br text-[11px] font-black ${rankTone}`}>
                    #{rank}
                  </span>
                  <span className="text-xl leading-none">{FLAG[code] ?? "🏳️"}</span>
                  <span className="text-sm font-bold text-white">{code}</span>
                  <span className="ml-auto flex items-baseline gap-2">
                    <AnimatedNumber value={hype} className="text-sm font-black tabular-nums text-white" />
                    <span className="text-[10px] uppercase tracking-wider text-white/40">hype</span>
                  </span>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}